import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, errText } from "@/lib/api";
import { toast, Toaster } from "sonner";

export default function AuthPage({ mode = "login" }) {
  const isLogin = mode === "login";
  const nav = useNavigate();
  const { login, register } = useAuth();
  const [email, setEmail] = useState(isLogin ? "admin@blackstarvirtualmint.com" : "");
  const [password, setPassword] = useState(isLogin ? "BlackStar2026!" : "");
  const [fullName, setFullName] = useState("");
  const [planId, setPlanId] = useState("free");
  const [cycle, setCycle] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { if (!isLogin) api.get("/plans").then((r)=>setPlans(r.data.plans)).catch(()=>{}); }, [isLogin]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
        toast.success("Welcome back to the Mint");
        nav("/app");
      } else {
        await register({ email, password, full_name: fullName || "Vault Holder" });
        toast.success("Vault provisioned");
        if (planId !== "free") {
          const plan = plans.find(p => p.id === planId);
          if (plan) {
            const lookup = cycle === "monthly" ? plan.monthly_lookup : plan.annual_lookup;
            const { data } = await api.post("/stripe/checkout", { lookup_key: lookup, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
            return;
          }
        }
        nav("/app");
      }
    } catch (e) {
      const msg = errText(e); setErr(msg); toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <Toaster theme="dark" richColors position="top-center" />
      <div className="hidden md:flex relative overflow-hidden grain items-end p-14 border-r border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#0f0f13] to-[#1a1408]" />
        <div className="relative z-10 max-w-md">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#d4af37] to-[#7a5f16] grid place-items-center font-display font-black text-black text-xl">★</div>
          <h2 className="font-display text-4xl mt-8 leading-tight">Enter the credit-initiated vault.</h2>
          <p className="text-white/60 mt-4">Every account is seeded by the database at state S06. No fiat printing. Redeem-anywhere ATM money orders.</p>
          <div className="mt-8 font-mono text-xs text-white/40 space-y-1">
            <div>▸ Access · 0.50% shares</div>
            <div>▸ Standard · 0.75% shares</div>
            <div>▸ Green · 0.95% shares</div>
            <div>▸ Professional · 1.25% shares</div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 overflow-y-auto">
        <form onSubmit={submit} className="w-full max-w-md" data-testid={isLogin ? "login-form" : "register-form"}>
          <div className="chip gold mb-6">{isLogin ? "SIGN IN" : "OPEN ACCOUNT"}</div>
          <h1 className="font-display text-4xl mb-2">{isLogin ? "Welcome back." : "Claim your vault."}</h1>
          <p className="text-white/60 mb-8 text-sm">{isLogin ? "Access the BlackStar Virtual Mint." : "A credit-initiated grant is waiting."}</p>

          {!isLogin && (
            <label className="block mb-4">
              <span className="label">Full name</span>
              <input data-testid="register-fullname-input" className="input mt-2" value={fullName} onChange={(e)=>setFullName(e.target.value)} placeholder="M. Agada" required/>
            </label>
          )}
          <label className="block mb-4">
            <span className="label">Email</span>
            <input data-testid={isLogin ? "login-email-input" : "register-email-input"} type="email" className="input mt-2" value={email} onChange={(e)=>setEmail(e.target.value)} required/>
          </label>
          <label className="block mb-4">
            <span className="label">Password</span>
            <input data-testid={isLogin ? "login-password-input" : "register-password-input"} type="password" className="input mt-2" value={password} onChange={(e)=>setPassword(e.target.value)} minLength={6} required/>
          </label>

          {!isLogin && (
            <>
              <div className="mb-3 flex items-center justify-between">
                <span className="label">Choose your tier</span>
                <div className="inline-flex bg-white/5 rounded-full p-0.5 border border-white/10">
                  <button type="button" data-testid="reg-cycle-monthly" onClick={()=>setCycle("monthly")} className={`px-3 py-1 rounded-full text-[11px] ${cycle==="monthly" ? "bg-[#d4af37] text-black" : "text-white/60"}`}>Monthly</button>
                  <button type="button" data-testid="reg-cycle-annual" onClick={()=>setCycle("annual")} className={`px-3 py-1 rounded-full text-[11px] ${cycle==="annual" ? "bg-[#d4af37] text-black" : "text-white/60"}`}>Annual</button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-2 mb-6">
                <PlanChoice id="free" name="Free · Vault only" pct={0} price={0} cycle={cycle} selected={planId==="free"} onSelect={setPlanId}/>
                {plans.map((p) => (
                  <PlanChoice key={p.id} id={p.id} name={p.name} pct={p.share_pct} price={cycle==="monthly" ? p.monthly : p.annual} cycle={cycle} selected={planId===p.id} onSelect={setPlanId}/>
                ))}
              </div>
            </>
          )}

          {err && <div className="text-[#ff3366] text-sm mb-4 font-mono">{err}</div>}
          <button data-testid={isLogin ? "login-submit-btn" : "register-submit-btn"} disabled={loading} className="btn-primary w-full">
            {loading ? "…" : (isLogin ? "Enter Vault" : (planId === "free" ? "Provision Vault" : "Provision & Checkout"))}
          </button>
          <div className="mt-6 text-sm text-white/60">
            {isLogin ? "First time?" : "Already have a vault?"}{" "}
            <Link data-testid="auth-switch-link" className="text-[#d4af37] link-underline" to={isLogin ? "/register" : "/login"}>
              {isLogin ? "Open account" : "Sign in"}
            </Link>
          </div>
          <Link to="/" data-testid="auth-back-home" className="mt-8 inline-block text-xs text-white/40 hover:text-white/80">← Back to the Mint</Link>
        </form>
      </div>
    </div>
  );
}

function PlanChoice({ id, name, pct, price, cycle, selected, onSelect }) {
  return (
    <button type="button" data-testid={`reg-plan-${id}`} onClick={()=>onSelect(id)}
      className={`text-left flex items-center justify-between p-3 rounded-lg border transition-colors ${selected ? "border-[#d4af37] bg-[#d4af37]/10" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
      <div>
        <div className="text-sm text-white">{name}</div>
        <div className="text-[11px] font-mono text-white/50">{pct > 0 ? `${pct.toFixed(2)}% share allocation` : "No share allocation"}</div>
      </div>
      <div className="font-mono text-sm text-[#d4af37]">
        {price === 0 ? "Free" : `$${price}/${cycle==="monthly"?"mo":"yr"}`}
      </div>
    </button>
  );
}
